import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import type { ReactNode } from "react";
import { useParams, useLocation } from "react-router-dom";
import { DismissRegular } from "@fluentui/react-icons";
import { useTranslations } from "@/hooks/useTranslations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCurrentPage } from "@/hooks/useCurrentPage";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner/LoadingSpinner";
import { useNavigation } from "./useNavigation";
import { NavigationNode } from "./NavigationNode/NavigationNode";
import { SearchBox } from "./SearchBox/SearchBox";
import type { INavigationNodeDto } from "@/types/filteredContentTypes";
import { getPath, normalizePathForCompare } from "./navigationPathUtils";
import styles from "./LeftNav.module.scss";

export function LeftNav(): ReactNode {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslations();
  const { setIsNavLoaded, isMobileNavOpen, setIsMobileNavOpen } = useCurrentPage();

  const locale = (params?.locale as string) || "en-US";
  const currentPath = location.pathname || "";
  const whatsNewPath = `/${locale}/whats-new`;
  const isWhatsNewPage =
    normalizePathForCompare(currentPath) === normalizePathForCompare(whatsNewPath);

  const [searchTerm, setSearchTerm] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    rootNodes,
    expandedItems,
    loadedChildren,
    loadingChildren,
    isLoading,
    error,
    searchResults,
    isSearching,
    handleToggle,
    handleLoadChildren,
    handleSearch,
    clearSearch,
    findPageTitle,
    ensureExpandedToPath,
  } = useNavigation(locale);

  // Set browser tab title
  const pageTitle = isWhatsNewPage ? t("whatsNewPage.title") : findPageTitle(currentPath);
  usePageTitle(pageTitle || t("navigation.defaultPageTitle"));

  const rootNodesToRender = useMemo((): INavigationNodeDto[] => {
    const whatsNewNode: INavigationNodeDto = {
      id: 0,
      key: `whats-new-${locale}`,
      name: t("whatsNewPage.title"),
      documentType: "genericPage",
      title: t("whatsNewPage.title"),
      url: whatsNewPath,
      hasChildren: false,
    };

    const hasWhatsNewAlready = rootNodes.some((n) => {
      if (n.key === whatsNewNode.key) {
        return true;
      }
      const nodePath = getPath(n.url, locale);
      return (
        nodePath &&
        normalizePathForCompare(nodePath) === normalizePathForCompare(whatsNewPath)
      );
    });

    if (hasWhatsNewAlready) {
      return rootNodes;
    }

    if (rootNodes.length === 0) {
      return [whatsNewNode];
    }

    const [first, ...rest] = rootNodes;
    return [first, whatsNewNode, ...rest];
  }, [locale, rootNodes, t, whatsNewPath]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!value || value.length < 2) {
        clearSearch();
        return;
      }

      // Debounce API call
      searchTimeoutRef.current = setTimeout(() => {
        void handleSearch(value);
      }, 300);
    },
    [handleSearch, clearSearch]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return (): void => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Update navigation loaded state for TopBar to show download button
  useEffect(() => {
    if (!isLoading && !error && rootNodes.length > 0) {
      setIsNavLoaded(true);
    } else {
      setIsNavLoaded(false);
    }

    return (): void => {
      setIsNavLoaded(false);
    };
  }, [isLoading, error, rootNodes.length, setIsNavLoaded]);

  // Auto-expand tree to the current page (best effort)
  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    // Don't auto-expand while showing search results
    if (searchTerm.length >= 2) {
      return;
    }

    // There's nothing to expand on the What's New page.
    if (isWhatsNewPage) {
      return;
    }

    void ensureExpandedToPath(currentPath);
  }, [currentPath, ensureExpandedToPath, isLoading, error, searchTerm, isWhatsNewPage]);

  // Close mobile nav on Escape key (only listen when drawer is open)
  useEffect(() => {
    if (!isMobileNavOpen) {
      return;
    }

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setIsMobileNavOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return (): void => document.removeEventListener("keydown", handleEscape);
  }, [isMobileNavOpen, setIsMobileNavOpen]);

  // Lock body scroll when mobile drawer is open to prevent background scrolling
  useEffect(() => {
    if (!isMobileNavOpen) {
      return;
    }

    document.body.style.overflow = "hidden";
    return (): void => {
      document.body.style.overflow = "";
    };
  }, [isMobileNavOpen]);

  // Determine what to display: search results (flat list) or tree view
  const isShowingSearchResults = searchTerm.length >= 2;

  const treeRef = useRef<HTMLDivElement>(null);
  const focusedKeyRef = useRef<string | null>(null);

  // After each render, enforce roving tabindex: only one tree item is tabbable (tabIndex=0),
  // all others are tabIndex=-1.
  useEffect(() => {
    const tree = treeRef.current;
    if (!tree) {
      return;
    }

    const items = Array.from(tree.querySelectorAll<HTMLElement>('[data-nav-tree-item]'));
    if (items.length === 0) {
      return;
    }

    // 1. If focus is currently inside the tree, keep that element as the tabbable item
    const activeEl = document.activeElement as HTMLElement | null;
    let tabbableItem: HTMLElement | undefined;
    if (activeEl && tree.contains(activeEl)) {
      tabbableItem = activeEl.closest('[data-nav-tree-item]') as HTMLElement | undefined;
    }

    // 2. Otherwise, try the last arrow-key-tracked item
    if (!tabbableItem && focusedKeyRef.current) {
      tabbableItem = items.find((el) => el.closest('[data-node-key]')?.getAttribute('data-node-key') === focusedKeyRef.current);
    }

    // 3. Fallback to first item only when nothing else matches
    if (!tabbableItem) {
      tabbableItem = items[0];
    }

    for (const item of items) {
      item.setAttribute('tabindex', item === tabbableItem ? '0' : '-1');
    }
  }, [expandedItems, loadedChildren, searchResults, rootNodes]);

  // Keyboard navigation handler for the tree (roving tabindex with arrow keys)
  const handleTreeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
    const tree = treeRef.current;
    if (!tree) {
      return;
    }

    const items = Array.from(tree.querySelectorAll<HTMLElement>('[data-nav-tree-item]'));
    const activeItem = (document.activeElement as HTMLElement | null)?.closest('[data-nav-tree-item]') as HTMLElement | null;
    const currentIndex = activeItem ? items.indexOf(activeItem) : -1;

    if (currentIndex === -1) {
      return;
    }

    // Helper to move focus and update roving tabindex
    const moveFocus = (newIndex: number): void => {
      const target = items[newIndex];
      if (!target) {
        return;
      }
      items[currentIndex]?.setAttribute('tabindex', '-1');
      target.setAttribute('tabindex', '0');
      target.focus();
      focusedKeyRef.current = target.closest('[data-node-key]')?.getAttribute('data-node-key') ?? null;
    };

    // Get the node key for ArrowLeft/Right expand/collapse
    const currentNodeKey = items[currentIndex]?.closest('[data-node-key]')?.getAttribute('data-node-key');

    let handled = false;

    switch (e.key) {
      case 'ArrowDown': {
        if (currentIndex < items.length - 1) {
          moveFocus(currentIndex + 1);
          handled = true;
        }
        break;
      }
      case 'ArrowUp': {
        if (currentIndex > 0) {
          moveFocus(currentIndex - 1);
          handled = true;
        }
        break;
      }
      case 'ArrowRight': {
        if (currentNodeKey && !expandedItems.has(currentNodeKey)) {
          handleToggle(currentNodeKey);
          const nodeChildren = loadedChildren.get(currentNodeKey);
          if (!nodeChildren && !loadingChildren.has(currentNodeKey)) {
            handleLoadChildren(currentNodeKey);
          }
          handled = true;
        }
        break;
      }
      case 'ArrowLeft': {
        if (currentNodeKey && expandedItems.has(currentNodeKey)) {
          handleToggle(currentNodeKey);
          handled = true;
        }
        break;
      }
      case 'Home': {
        if (currentIndex !== 0) {
          moveFocus(0);
          handled = true;
        }
        break;
      }
      case 'End': {
        if (currentIndex !== items.length - 1) {
          moveFocus(items.length - 1);
          handled = true;
        }
        break;
      }
      case 'Enter':
      case ' ': {
        const activeEl = document.activeElement as HTMLElement;
        if (activeEl) {
          activeEl.click();
          handled = true;
        }
        break;
      }
    }

    if (handled) {
      e.preventDefault();
    }
  }, [expandedItems, loadedChildren, loadingChildren, handleToggle, handleLoadChildren]);

  return (
    <nav
      className={styles.leftNav}
      aria-label={t("navigation.documentationNav")}
      data-open={isMobileNavOpen}
    >
      <button
        className={styles.mobileCloseButton}
        onClick={(): void => setIsMobileNavOpen(false)}
        aria-label={t("common.close")}
      >
        <DismissRegular />
      </button>

      <SearchBox
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={t("navigation.filterByTitle")}
        ariaLabel={t("navigation.filterNavByTitle")}
      />

      <div className={styles.treeContainer}>
        {error && <div className={styles.error}>{error}</div>}

        {(isLoading || isSearching) && (
          <LoadingSpinner
            message={isSearching ? t("navigation.searching") : t("navigation.loadingNavigation")}
          />
        )}

        {!isLoading && !error &&
          (isShowingSearchResults && !isSearching && searchResults.length === 0 && searchTerm.length >= 2 ? (
            <div className={styles.noResults} role="status">
              {t("navigation.noResults")}
            </div>
          ) : (
            <div className={styles.treeList} role="tree" tabIndex={-1} ref={treeRef} onKeyDown={handleTreeKeyDown}>
              {isShowingSearchResults
                ? // Flat list of search results
                  searchResults.map((node) => (
                    <NavigationNode
                      key={node.key}
                      node={node}
                      currentPath={currentPath}
                      searchTerm={searchTerm}
                      locale={locale}
                      expandedItems={expandedItems}
                      loadedChildren={loadedChildren}
                      loadingChildren={loadingChildren}
                      onToggle={handleToggle}
                      onLoadChildren={handleLoadChildren}
                      level={0}
                      isSearchResult
                    />
                  ))
                : // Normal tree view
                  rootNodesToRender.map((node) => (
                    <NavigationNode
                      key={node.key}
                      node={node}
                      currentPath={currentPath}
                      searchTerm=""
                      locale={locale}
                      expandedItems={expandedItems}
                      loadedChildren={loadedChildren}
                      loadingChildren={loadingChildren}
                      onToggle={handleToggle}
                      onLoadChildren={handleLoadChildren}
                      level={0}
                    />
                  ))}
            </div>
          ))}
      </div>
    </nav>
  );
}
